import DeleteModal from '@/components/CommonComponents/DeleteModal';
import { AppDispatch, RootState } from '@/store';
import { fetchProjectsList } from '@/store/slices/projectsSlice/projectsSlice';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useExportFile } from '@/hooks/useExportFile';
import { usePermission } from '@/hooks/usePermission';
import { projectsService } from '@/services/projectsService/projectsService';
import { getAllRoles } from '@/store/slices/adminUsersSlice/adminUserSlice';
import { fetchMetaData } from '@/store/slices/metaDataSlice/metaDataSlice';
import ServerSearchFilter from '../CommonComponents/ServerSearchFilter';
import PageHeaderWrapper from '../Layout/PageHeaderWrapper';
import { AllProjects } from './AllProjects';

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { list, loading, error } = useSelector((state: RootState) => state.projects);
  const projectsData = list.data;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [filterState, setFilterState] = useState<Record<string, any>>({});
  const [adminIdToDelete, setAdminIdToDelete] = useState<string | null>(null);
  const [projectIdToDelete, setProjectIdToDelete] = useState<string | null>(null);

  const { exportFile } = useExportFile();

  const permissions = {
    create: usePermission('write', 'projects'),
    export: usePermission('export', 'projects'),
  };

  useEffect(() => {
    dispatch(fetchMetaData());
    dispatch(getAllRoles());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchProjectsList({ page: currentPage, limit: pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handleAddProject = () => {
    navigate('/dashboard/project-management/create');
  };

  const handleExport = () => {
    exportFile(() => projectsService.exportProjects(), `projects_${Date.now()}.csv`);
  };

  const totalPages = list.totalPages || 0;
  const LIMIT_STEPS = [25, 50, 75];

  const openDeleteModal = (adminId: string) => {
    setAdminIdToDelete(adminId);
    setIsDeleteModalOpen(true);
  };

  const handleFilterChange = (filters: Record<string, any>) => {
    const search = filters.search || '';
    const limit = Number(filters?.limit) || 25;
    let statusFetched = filters.status;

    statusFetched = statusFetched ? statusFetched === 'Active' : null;

    const needsUpdate = pageSize !== limit || currentPage !== 1;

    if (needsUpdate) {
      setPageSize(limit);
      setCurrentPage(1);
    }
    if (search || statusFetched !== null || !needsUpdate) {
      dispatch(fetchProjectsList({ page: 1, limit, searchQuery: search, status: statusFetched }));
    }
  };

  const loadMore = () => {
    const idx = LIMIT_STEPS.indexOf(pageSize);
    if (idx < LIMIT_STEPS.length - 1) {
      setPageSize(LIMIT_STEPS[idx + 1]);
      setCurrentPage(1);
    }
  };

  const loadLess = () => {
    setPageSize(LIMIT_STEPS[0]);
    setCurrentPage(1);
  };

  const canLoadMore =
    LIMIT_STEPS.indexOf(pageSize) < LIMIT_STEPS.length - 1 &&
    (list.total || 0) > (projectsData?.length || 0);
  const canLoadLess = pageSize > LIMIT_STEPS[0];

  return (
    <PageHeaderWrapper
      title="Project Management"
      description="Manage all projects you have access too"
      onAdd={handleAddProject}
      onExport={handleExport}
      addLabel="Add Project"
      exportLabel="Export"
      permissions={permissions}
    >
      {/* Search + Filter */}

      <ServerSearchFilter
        fields={[
          {
            type: 'search',
            key: 'search',
            placeholder: 'Search items...',
          },
          {
            type: 'radio-select',
            key: 'status',
            label: 'Status',
            options: ['Active', 'Inactive'],
          },
          {
            type: 'radio-select',
            key: 'limit',
            label: 'limit',
            options: ['25', '50', '75'],
            defaultValue: '25',
          },
        ]}
        onChange={handleFilterChange}
        controlledValues={{ limit: String(pageSize) }}
      />
      <AllProjects
        projectsData={projectsData}
        totalPages={list.totalPages || 0}
        loadedCount={projectsData?.length || 0}
        limit={pageSize}
        currentPage={currentPage}
        loadMore={loadMore}
        loadLess={loadLess}
        total={list.total || 0}
        canLoadMore={canLoadMore}
        canLoadLess={canLoadLess}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProjectIdToDelete(null);
        }}
        onDelete={() => {
          if (projectIdToDelete) {
            setIsDeleteModalOpen(false);
            setAdminIdToDelete(null);
          }
        }}
      />
    </PageHeaderWrapper>
  );
};

export default Projects;
